import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Small inline toolbar for AI note transformations.
 * Calls onResult(text) after a successful transform.
 */
export default function AiNoteToolbar({ getText, onResult, disabled }) {
  const [busy, setBusy] = useState("");

  const run = async (action) => {
    const text = (getText?.() || "").trim();
    if (!text) {
      toast.error("Write something first — then I can help polish it.");
      return;
    }
    setBusy(action);
    try {
      const { data } = await api.post("/ai/note-helper", { text, action });
      onResult?.(data.text);
      toast.success("Done");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI request failed");
    }
    setBusy("");
  };

  const Btn = ({ action, children }) => (
    <Button
      type="button"
      data-testid={`ai-note-${action}`}
      variant="ghost"
      size="sm"
      disabled={disabled || !!busy}
      onClick={() => run(action)}
      className="h-7 gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-gold hover:bg-gold/5"
    >
      {busy === action ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
      {children}
    </Button>
  );

  return (
    <div className="flex items-center gap-1 border-t border-bone pt-2 -mt-1" data-testid="ai-note-toolbar">
      <span className="text-[10px] uppercase tracking-[0.25em] text-gold/80 font-semibold pr-2">Ask AI</span>
      <Btn action="summarize">Summarize</Btn>
      <Btn action="expand">Expand</Btn>
      <Btn action="cleanup">Clean up</Btn>
    </div>
  );
}
